import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { getFileIcon } from '@/components/Form/Shared/FileItem/shared/GenericFilePreview'
import { DataTableCustomized } from '@/components/ui/data-table-customized'
import type { DataTableColumn } from '@/components/ui/data-table-customized/types'
import type { DataTableAction } from '@/components/ui/data-table-customized/types/actions'
import { useConfirmation } from '@/contexts/confirmation'
import { useModal } from '@/contexts/modal'
import { useFilePreviewWithPresigned } from '@/hooks/useFilePreviewWithPresigned'
import AddIcons from '@/icons/AddIcons'
import { DOCUMENT_TYPES } from '@/modules/Employee/constants'
import {
    deleteEmployeeDocument,
    getEmployeeDocuments,
} from '@/modules/Employee/services'
import { Attachment } from '@/modules/Upload/types'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import { useQueryClient } from '@tanstack/react-query'
import TrashIcons from '@/icons/DeleteOutlineIcons'
import React, { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { Button, Loading } from 'vnetwork-ui'
import { QUERY_KEY_COMBINATIONS } from '@/constants/reactQueryKeys'

interface ProfileProps {
    employeeId?: number
    isSelf?: boolean
}

// Flat row type for the table
interface DocumentRow extends Attachment {
    document_type: string
}

const Profile: React.FC<ProfileProps> = ({ employeeId, isSelf = false }) => {
    const params = useParams()
    const id = employeeId || Number(params.id)
    const { t } = useTranslation()
    const queryClient = useQueryClient()
    const { setModal } = useModal()
    const { openFilePreview } = useFilePreviewWithPresigned()
    const { setConfirmation } = useConfirmation()

    const queryKey = isSelf
        ? [QUERY_KEYS.CURRENT_USER_PROFILE, 'documents']
        : [QUERY_KEYS.EMPLOYEES, 'documents', id]

    const { data: employeeDocuments } = useQueryWithHandler({
        queryKey,
        queryFn: () => getEmployeeDocuments({ id }),
        enabled: !!id,
        loadingComponent: (
            <div className="flex justify-center items-center min-h-[300px]">
                <Loading.CircularProgress width={40} height={40} />
            </div>
        ),
    })

    const { mutateAsync: deleteAttachment } = useMutationWithHandler(
        {
            mutationFn: ({ attachmentId }: { attachmentId: number }) =>
                deleteEmployeeDocument({ id, attachmentId }),
        },
        {
            reactQueryKeys: QUERY_KEY_COMBINATIONS.EMPLOYEE_WITH_DOCUMENT,
            successMessage: 'attachment-deleted-success',
            errorMessage: 'error-occurred',
            onSuccess: () => {
                queryClient.refetchQueries({ queryKey })
            },
        }
    )

    const formatFileSize = useCallback((bytes: number) => {
        if (bytes === 0) return '0 B'
        const k = 1024
        const sizes = ['B', 'KB', 'MB', 'GB']
        const i = Math.floor(Math.log(bytes) / Math.log(k))
        return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i]
    }, [])

    // Flatten all documents into a single row list
    const rows: DocumentRow[] = useMemo(() => {
        if (!employeeDocuments) return []
        return employeeDocuments.flatMap((doc) =>
            (doc.attachments || []).map((att) => ({
                ...(att as Attachment),
                document_type: doc.document_type,
            }))
        )
    }, [employeeDocuments])

    const handlePreview = useCallback(
        async (attachment: Attachment) => {
            await openFilePreview(attachment)
        },
        [openFilePreview]
    )

    const handleDeleteAttachment = useCallback(
        async (attachment: Attachment) => {
            setConfirmation({
                open: true,
                title: t('delete-attachment'),
                description: t('confirm-delete-attachment-message', {
                    fileName: attachment.attachment_file_name,
                }),
                onConfirm: async () => {
                    await deleteAttachment({
                        attachmentId: attachment.attachment_id,
                    })
                },
            })
        },
        [deleteAttachment, setConfirmation, t]
    )

    const handleAddDocument = useCallback(() => {
        setModal('Employee/AddDocumentModal/index', {
            employeeId: Number(id),
            onSuccess: () => {
                queryClient.refetchQueries({ queryKey })
            },
        })
    }, [setModal, id, queryClient])

    const [selectedKeys, setSelectedKeys] = useState<string[]>([])

    // Bulk delete selected attachments
    const handleBulkDelete = useCallback(
        (selectedRows: DocumentRow[]) => {
            setConfirmation({
                open: true,
                title: t('delete-attachment'),
                description: t('confirm-delete-attachment-message', {
                    fileName: `${selectedRows.length} ${t('files')}`,
                }),
                onConfirm: async () => {
                    await Promise.all(
                        selectedRows.map((row) =>
                            deleteAttachment({
                                attachmentId: row.attachment_id,
                            })
                        )
                    )
                    setSelectedKeys([])
                },
            })
        },
        [deleteAttachment, setConfirmation, t]
    )

    const docTypeOptions = useMemo(() => DOCUMENT_TYPES(t), [t])

    const columns: DataTableColumn<DocumentRow>[] = useMemo(
        () => [
            {
                key: 'attachment_file_name',
                title: t('employees.file'),
                render: (_val, row) => (
                    <div className="flex items-center gap-2 min-w-0">
                        <div className="w-6 h-6 bg-indigo-50 rounded flex items-center justify-center flex-shrink-0">
                            {getFileIcon(row.attachment_file_type)}
                        </div>
                        <button
                            type="button"
                            className="text-primary hover:underline cursor-pointer truncate text-left bg-transparent border-0 p-0"
                            onClick={() => handlePreview(row)}
                        >
                            {row.attachment_file_name}
                        </button>
                    </div>
                ),
            },
            {
                key: 'document_type',
                title: t('employees.document_type'),
                width: 160,
                render: (_val, row) => {
                    const label =
                        docTypeOptions.find(
                            (o) => o.value === row.document_type
                        )?.label || row.document_type
                    return (
                        <span className="inline-block px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded-full">
                            {label}
                        </span>
                    )
                },
            },
            {
                key: 'attachment_file_size',
                title: t('size'),
                width: 100,
                render: (_val, row) => (
                    <span className="text-gray-400 text-xs">
                        {row.attachment_file_size
                            ? formatFileSize(row.attachment_file_size)
                            : '-'}
                    </span>
                ),
            },
        ],
        [t, docTypeOptions, handlePreview, formatFileSize]
    )

    const actions: DataTableAction<DocumentRow>[] = useMemo(
        () => [
            {
                key: 'preview',
                label: t('preview'),
                onClick: (row) => handlePreview(row),
            },
            {
                key: 'delete',
                label: t('delete'),
                variant: 'danger',
                onClick: (row) => handleDeleteAttachment(row),
            },
        ],
        [t, handlePreview, handleDeleteAttachment]
    )

    return (
        <div className="space-y-3">
            <div className="flex justify-between items-center">
                <span className="text-sm text-gray-500">
                    {rows.length} {t('files')}
                </span>
                <Button
                    startIcon={<AddIcons />}
                    variant="outlined"
                    size="small"
                    onClick={handleAddDocument}
                    className="text-primary hover:bg-primary/10"
                >
                    {t('employees.add_document')}
                </Button>
            </div>

            <DataTableCustomized
                columns={columns}
                rows={rows}
                rowKey={(row) => String(row.attachment_id)}
                actions={actions}
                size="small"
                emptyMessage={t('no-documents-found')}
                selection={{
                    selectedKeys,
                    onSelectionChange: setSelectedKeys,
                    getRowKey: (row) => String(row.attachment_id),
                    actions: [
                        {
                            key: 'bulk-delete',
                            label: t('delete'),
                            icon: <TrashIcons className="w-4 h-4" />,
                            variant: 'danger',
                            onClick: handleBulkDelete,
                        },
                    ],
                }}
            />
        </div>
    )
}

export default Profile
